/**
 * Theme Scroll Marquee Helper
 * Initializes horizontal marquee animations controlled by GSAP ScrollTrigger,
 * and handles scroll-based visibility animations (fade-up-scroll).
 * It searches the DOM for elements with the attribute [data-scroll-marquee].
 */

(function () {
  // Check if required libraries are loaded
  if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
    console.warn("GSAP or ScrollTrigger are not loaded. Animations will not function.");
    return;
  }

  gsap.registerPlugin(ScrollTrigger);

  /**
   * Initializes the Marquee scroll-synced animation for a single element.
   * @param {HTMLElement} wrapper - The main section wrapper element.
   * @param {number} index - The index of the marquee, used for unique ID.
   */
  function initializeMarquee(wrapper, index) {
    // Assign a unique ID for reinitialization logic
    const id = `marquee-${index}`;
    let st = ScrollTrigger.getById(id);

    // If ScrollTrigger already exists (due to theme editor load), kill it before recreating.
    if (st) {
      st.kill();
    }

    // 1. Get Elements and Settings
    // The target is the element containing the text to be moved
    const target = wrapper.querySelector('.built-in-goodness--title') || wrapper.querySelector('.marquee-title');
    if (!target) return;

    // Read the speed factor from the data attribute (default to 0.5)
    const reductionFactor = parseFloat(wrapper.dataset.scrollMarqueeSpeed) || 0.5;

    // 2. Calculate Scroll Distance
    const contentWidth = target.scrollWidth;
    const wrapperWidth = wrapper.offsetWidth;

    // Total distance needed to scroll the entire hidden content across the screen.
    const totalScrollDistance = contentWidth - wrapperWidth;

    // Apply the reduction factor to control the speed relative to the scroll.
    const finalScrollDistance = totalScrollDistance * reductionFactor;

    // If the text is not wider than the container, no need to animate.
    if (totalScrollDistance <= 1) return;

    // 3. Initialize GSAP Animation
    gsap.fromTo(target, {
      x: 0, // Start position: Text is visible from the beginning (aligned left)
    }, {
      x: -finalScrollDistance, // End position: scroll to the left by the calculated distance
      ease: "none",
      scrollTrigger: {
        id: id, // Assign unique ID for killing/restarting
        trigger: wrapper,
        start: "top bottom-=16%", // Start animation when the top of the section hits the bottom of the viewport
        // FIX: Extends the scroll duration by 100% of the viewport height.
        // This ensures the marquee continues to move until the element is completely out of sight.
        end: "bottom top-=100%",
        scrub: 0.5,           // Smoothly syncs the animation with scroll movement (0.5s smoothing)
        invalidateOnRefresh: true, // Recalculate on resize/orientation change
      }
    });
  }

  /**
   * Initializes the column classification and Intersection Observer for the fade-up-scroll effect.
   */
  function initializeFadeUpScroll() {
    // 1. Column Classification (for Built-in Goodness section)
    const container = document.querySelector('[data-columns-container]'); // Use data attribute for robust selection
    if (container) {
        // Logic to classify columns (from built-in-goodness.js)
        const count = container.dataset.columnCount || container.children.length; // Use Liquid count if available
        if (parseInt(count) === 4) {
            container.classList.add("four-built-in-goodness--benefit-columns");
        }
        if (parseInt(count) === 5) {
            container.classList.add("five-built-in-goodness--benefit-columns");
        }

        // 2. Intersection Observer for scroll
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('visible');
                    // Stop observing once visible to save performance
                    observer.unobserve(entry.target);
                }
            });
        }, {
            threshold: 0.1,
            rootMargin: '0px 0px -100px 0px'
        });
  
        document.querySelectorAll('.fade-up-scroll').forEach(el => {
            observer.observe(el);
        });
    }
  }

  /**
   * Main function to find and initialize all animation elements on the page.
   */
  function initializeAllAnimations() {
    // 1. Initialize all Marquee elements
    const marqueeWrappers = document.querySelectorAll('[data-scroll-marquee]');
    marqueeWrappers.forEach((wrapper, index) => {
      initializeMarquee(wrapper, index);
    });
    // 2. Initialize Fade-up elements
    initializeFadeUpScroll();
  }

  // --- Initialization Sequence ---
  // Ensure the DOM is fully loaded before trying to calculate element sizes.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeAllAnimations);
  } else {
    // DOM is already loaded (common on mobile)
    initializeAllAnimations();
  }

  // Add window load event for additional reliability on mobile
  window.addEventListener('load', function() {
    ScrollTrigger.refresh();
  });

  // Re-run initialization when sections are added/updated in the theme editor
  if (Shopify.designMode) {
    document.addEventListener('shopify:section:load', initializeAllAnimations);
  }

  // Re-run initialization when facets/filters are updated (AJAX content update)
  document.addEventListener('facets:updated', function() {
    // Small delay to ensure DOM is fully updated
    setTimeout(function() {
      initializeAllAnimations();
    }, 100);
  });

  // Re-run initialization when infinite scroll loads more products
  document.addEventListener('infinite-scroll:loaded', function() {
    // Small delay to ensure DOM is fully updated
    setTimeout(function() {
      initializeAllAnimations();
    }, 100);
  });

  // Refresh ScrollTrigger on resize/orientation change for mobile
  let resizeTimer;
  window.addEventListener('resize', function() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function() {
      ScrollTrigger.refresh();
    }, 250);
  });

})();